// components/EarlyBirdCountdown.jsx
// Early-bird countdown to the C# Foundation launch. One shared hook, two
// renders: the offer card (enroll page — the caller passes price + checkout
// as children, which always render) and a compact mini block (grid card).
// The offer framing (pill, headline, subline, tiles) disappears once the
// launch moment passes; the mini block hides entirely.
import { useEffect, useState } from "react";
import { THEME } from "../theme";

// Launch moment in IST (the audience is India-based).
export const LAUNCH_AT = new Date("2026-08-01T00:00:00+05:30");

const calc = () => {
  const ms = LAUNCH_AT.getTime() - Date.now();
  if (ms <= 0) return { days: 0, hours: 0, minutes: 0, expired: true };
  return {
    days: Math.floor(ms / 86_400_000),
    hours: Math.floor((ms % 86_400_000) / 3_600_000),
    minutes: Math.floor((ms % 3_600_000) / 60_000),
    expired: false,
  };
};

// Ticks once a minute (no seconds), cleared on unmount.
export function useEarlyBirdCountdown() {
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setLeft(calc()), 60_000);
    return () => clearInterval(id);
  }, []);
  return left;
}

const pad2 = (n) => String(n).padStart(2, "0");

const Bolt = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
    <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);

const Pill = ({ children }) => (
  <span style={{ fontFamily: THEME.fonts.body, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", background: THEME.orange, color: "#fff", padding: "4px 12px", borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 5 }}>
    <Bolt /> {children}
  </span>
);

const Tiles = ({ tiles, numSize, radius, padding, labelSize }) => (
  <div style={{ display: "flex", gap: radius >= 12 ? 10 : 8 }}>
    {tiles.map(([num, label]) => (
      <div key={label} style={{ flex: 1, background: THEME.ink, borderRadius: radius, padding, textAlign: "center" }}>
        <div style={{ fontFamily: THEME.fonts.mono, fontSize: numSize, fontWeight: 600, color: "#fff", lineHeight: 1.15 }}>
          {num}
        </div>
        <div style={{ fontFamily: THEME.fonts.body, fontSize: labelSize, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: THEME.grey, marginTop: 3 }}>
          {label}
        </div>
      </div>
    ))}
  </div>
);

// Enroll-page offer band: a full-width horizontal flex row. Part A (framing
// copy) and Part B (tiles) show while showFraming && !expired; Part C (the
// caller's price row + checkout button) always renders, so post-launch the
// band slims down to price + Enroll now. flex-wrap stacks the parts on
// narrow screens; Part C's hairline flips from left to top when wrapped.
export function EarlyBirdOfferBlock({ style, showFraming = true, children }) {
  const { days, hours, minutes, expired } = useEarlyBirdCountdown();
  const framing = showFraming && !expired;
  if (!framing && !children) return null;

  return (
    <div style={{ background: THEME.coral, border: `1px solid ${THEME.coralBorder}`, borderRadius: THEME.radius, padding: "20px 24px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 24, ...style }}>
      {framing && (
        <div style={{ flex: "1.4 1 260px", display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Pill>Early bird</Pill>
            <span style={{ fontFamily: THEME.fonts.body, fontSize: 12, fontWeight: 500, color: THEME.darkOrange }}>
              Pre-order open now
            </span>
          </div>
          <div style={{ fontFamily: THEME.fonts.body, fontSize: 17, fontWeight: 500, color: THEME.ink }}>
            Lock the early-bird price before it ends.
          </div>
          <div style={{ fontFamily: THEME.fonts.body, fontSize: 13, color: THEME.grey }}>
            C# content goes live 1st August. Price rises at launch.
          </div>
        </div>
      )}
      {framing && (
        <div style={{ flex: "0 1 auto", width: 236, alignSelf: "center" }}>
          <Tiles
            tiles={[
              [pad2(days), "Days"],
              [pad2(hours), "Hrs"],
              [pad2(minutes), "Min"],
            ]}
            numSize={26}
            radius={12}
            padding={12}
            labelSize={10}
          />
        </div>
      )}
      {children && (
        <>
          <style>{`
            .eb-band-c{flex:1 1 240px;display:flex;flex-direction:column;gap:8px;justify-content:center;${framing ? `border-left:1px solid ${THEME.coralBorder};padding-left:22px;` : ""}}
            @media (max-width:760px){
              .eb-band-c{border-left:none;padding-left:0;${framing ? `border-top:1px solid ${THEME.coralBorder};padding-top:16px;` : ""}}
            }
          `}</style>
          <div className="eb-band-c">{children}</div>
        </>
      )}
    </div>
  );
}

// Grid-card mini block: coral sub-card with small tiles, no price, no CTA.
// Hidden entirely when expired.
export function EarlyBirdMiniBlock({ style }) {
  const { days, hours, minutes, expired } = useEarlyBirdCountdown();
  if (expired) return null;

  return (
    <div style={{ background: THEME.coral, border: `1px solid ${THEME.coralBorder}`, borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 10, ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Pill>Early bird</Pill>
        <span style={{ fontFamily: THEME.fonts.body, fontSize: 11.5, fontWeight: 500, color: THEME.darkOrange }}>
          Ends at launch
        </span>
      </div>
      <Tiles
        tiles={[
          [pad2(days), "Days"],
          [pad2(hours), "Hrs"],
          [pad2(minutes), "Min"],
        ]}
        numSize={17}
        radius={8}
        padding="8px 6px"
        labelSize={9}
      />
    </div>
  );
}
